import { Children, cloneElement, forwardRef, isValidElement, useId, type InputHTMLAttributes, type ReactElement, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import { classNames } from "./utils";

export interface FieldProps {
  children: ReactNode;
  className?: string;
  error?: ReactNode;
  hint?: ReactNode;
  label: ReactNode;
  required?: boolean;
}

export function Field({ children, className, error, hint, label, required }: FieldProps) {
  const generatedId = useId();
  const generatedControlId = `${generatedId}-control`;
  const hintId = `${generatedId}-hint`;
  const errorId = `${generatedId}-error`;
  const fieldDescriptionIds = [hint ? hintId : undefined, error ? errorId : undefined].filter(Boolean).join(" ");
  const describedChildren = Children.map(children, (child) => {
    if (!isValidElement(child)) return child;
    const control = child as ReactElement<{ id?: string; "aria-describedby"?: string; "aria-invalid"?: boolean | "true" | "false" }>;
    const describedBy = [control.props["aria-describedby"], fieldDescriptionIds].filter(Boolean).join(" ");
    return cloneElement(control, {
      id: control.props.id ?? generatedControlId,
      ...(describedBy ? { "aria-describedby": describedBy } : {}),
      ...(error ? { "aria-invalid": true } : {}),
    });
  });
  const labelledControl = Children.toArray(describedChildren).find(isValidElement) as ReactElement<{ id?: string }> | undefined;
  const controlId = labelledControl?.props.id ?? generatedControlId;

  return (
    <div className={classNames("am-field", Boolean(error) && "am-field--error", className)}>
      <label className="am-field__label" htmlFor={controlId}>{label}{required && <span aria-hidden="true"> *</span>}</label>
      {describedChildren}
      {hint && <span className="am-field__hint" id={hintId}>{hint}</span>}
      {error && <span className="am-field__error" id={errorId}>{error}</span>}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...props }, ref) {
  return <input className={classNames("am-input", className)} ref={ref} {...props} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...props }, ref) {
  return <textarea className={classNames("am-input am-textarea", className)} ref={ref} {...props} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ children, className, ...props }, ref) {
  return <select className={classNames("am-input am-select", className)} ref={ref} {...props}>{children}</select>;
});

export interface ToggleProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  description?: ReactNode;
  label: ReactNode;
}

export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(function Toggle({ className, description, id, label, ...props }, ref) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  return (
    <label className={classNames("am-toggle", className)} htmlFor={inputId}>
      <input id={inputId} ref={ref} type="checkbox" {...props} />
      <span aria-hidden="true" className="am-toggle__control" />
      <span className="am-toggle__copy"><strong>{label}</strong>{description && <span>{description}</span>}</span>
    </label>
  );
});
